import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { useOrgTimezone, formatLocalDateInTz, formatDateInTz, getTodayInTz } from '../../utils/timezone';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { OrderLineTable, OrderDespatchCard, OrderDrawdownCard, OrderShipmentsCard } from '../../components/nursery';
import {
  ShoppingCart, CheckCircle, Package, FileText,
  Calendar, User, Truck, Hash, Landmark, ListPlus, Download, XCircle, Tag, Wand2, Plus, Undo2,
} from 'lucide-react';
import { CancelOrderDialog } from '../../components/nursery/CancelOrderDialog';
import { RepriceOrderDialog } from '../../components/nursery/RepriceOrderDialog';
import { CounterPaymentCard } from '../../components/nursery/counter/CounterPaymentCard';
import { CashRefundDialog } from '../../components/nursery/counter/CashRefundDialog';
import { CreditNoteDialog } from '../../components/nursery/accounts/CreditNoteDialog';
import { AccountHoldCard } from '../../components/nursery/accounts/AccountHoldCard';
import { OrderDepositCard } from '../../components/nursery/accounts/OrderDepositCard';
import { FreightChoiceFields } from '../../components/nursery/accounts/FreightChoiceFields';
import { EMPTY_FREIGHT_CHOICE, freightChoiceFrom, type FreightChoiceDraft } from '../../utils/freightChoice';
import nurseryService from '../../services/nursery';
import nurseryAccountsService from '../../services/nurseryAccounts';
import { PaymentStateBadge } from '../../components/nursery/accounts/AccountBits';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import { useStockSeat } from '../../hooks/useStockSeat';
import { useSellSeat } from '../../hooks/useSellSeat';
import { useCrmSeat } from '../../hooks/useCrmSeat';
import CrmPanel from '../../components/sales/CrmPanel';
import { errorMessage } from '../../utils/apiResponse';
import { downloadBlob } from '../../utils/downloadBlob';
import { addDays } from '../../utils/payPeriod';
import { money, num } from '../../utils/nurseryMoney';

const INVOICE_TERMS_DAYS = 30;
import type {
  NurseryOrder, NurseryOrderStatus, NurseryOrderLine, NurseryOrderLineAllocation, AvailableStock, NurseryInvoice,
} from '../../types/nursery';
import type { PaymentState, CreditNoteRequest, CreditRequestAccess } from '../../types/nurseryAccounts';

const PO_MAX = 100;

/** Before it leaves the yard. An invoiced order is credited, not cancelled. */
const CANCELLABLE_STATUSES: NurseryOrderStatus[] = ['draft', 'confirmed', 'partially_allocated', 'allocated', 'picking', 'picked'];

/** Prices can still move until the trees leave, or the invoice is authorised. */
const REPRICEABLE_STATUSES = CANCELLABLE_STATUSES;

/**
 * Gone, but repriceable once its invoice is voided: the picks stand, so the
 * prices are put right and Create Invoice bills them again. The server also
 * refuses one paid online, which was charged at the old prices.
 */
const DESPATCHED_STATUSES: NurseryOrderStatus[] = ['shipped', 'completed'];

/** Before picking starts: quantities change on the order sheet, and allocations can be released. */
const EDITABLE_STATUSES: NurseryOrderStatus[] = ['draft', 'confirmed', 'partially_allocated', 'allocated'];
const RELEASABLE_STATUSES: NurseryOrderStatus[] = ['confirmed', 'partially_allocated', 'allocated'];

/** Paid, part paid and overdue are worked out from the balance and the due date; they are never stored. */
const invoicePaymentState = (inv: NurseryInvoice, today: string): PaymentState => {
  if (inv.status === 'draft') return 'draft';
  if (inv.status === 'cancelled') return 'void';
  if (inv.status === 'paid') return 'paid';
  if (inv.status === 'overdue' || (inv.due_date && inv.due_date.slice(0, 10) < today)) return 'overdue';
  return num(inv.amount_paid) > 0 ? 'part_paid' : 'open';
};

const STATUS_COLORS: Record<NurseryOrderStatus, string> = {
  draft:                'bg-neutral-100 text-neutral-700',
  confirmed:            'bg-blue-100 text-blue-700',
  allocated:            'bg-indigo-100 text-indigo-700',
  partially_allocated:  'bg-amber-100 text-amber-700',
  picking:              'bg-yellow-100 text-yellow-700',
  picked:               'bg-teal-100 text-teal-700',
  shipped:              'bg-cyan-100 text-cyan-700',
  invoiced:             'bg-green-100 text-green-700',
  completed:            'bg-green-100 text-green-800',
  cancelled:            'bg-red-100 text-red-700',
};

function OrderDetail() {
  const { t } = useTranslation('nursery', { keyPrefix: 'OrderDetail' });
  const timezone = useOrgTimezone();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  // Logistics (allocation, releasing, undoing picking) is the nursery's; selling
  // (entering, confirming, cancelling, repricing) is the Sales module's (useSellSeat).
  const { hasAccess: canManage } = useStockSeat('manage');
  const { hasAccess: canSell } = useSellSeat('sell');
  const { hasAccess: canSellAdmin } = useSellSeat('admin');
  // Notes, photos and issues on the order and its lines (Sales, or Nursery operator and up).
  const { hasAccess: hasCrm } = useCrmSeat();
  // The money side is the Accounts module's. Without a seat there, a nursery
  // manager still sees the order's invoices and downloads them.
  const { hasAccess: canAccounts } = useModuleLicense('accounts', ['user', 'admin']);
  const { hasAccess: isAccountsAdmin } = useModuleLicense('accounts', 'admin');
  const [pdfBusy, setPdfBusy] = useState<number | null>(null);

  const [order, setOrder] = useState<NurseryOrder | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Allocation modal state
  const [showAllocate, setShowAllocate] = useState(false);
  const [allocLineId, setAllocLineId] = useState<number | null>(null);
  const [availableStock, setAvailableStock] = useState<AvailableStock[] | null>(null);
  const [allocLotId, setAllocLotId] = useState<number | ''>('');
  const [allocQuantity, setAllocQuantity] = useState<number | ''>('');
  const [allocSubmitting, setAllocSubmitting] = useState(false);
  const [allocError, setAllocError] = useState<string | null>(null);

  // Invoice modal state
  const [showInvoice, setShowInvoice] = useState(false);
  const [invoiceDate, setInvoiceDate] = useState('');
  const [invoiceDueDate, setInvoiceDueDate] = useState('');
  // An outside carrier's price, typed when the order is invoiced.
  const [invoiceFreight, setInvoiceFreight] = useState('');
  const [freightChoice, setFreightChoice] = useState<FreightChoiceDraft>(EMPTY_FREIGHT_CHOICE);
  const [invoiceSubmitting, setInvoiceSubmitting] = useState(false);
  const [invoiceError, setInvoiceError] = useState<string | null>(null);

  // Purchase order number, edited in place
  const [poDraft, setPoDraft] = useState<string | null>(null);
  const [poSaving, setPoSaving] = useState(false);
  const [poError, setPoError] = useState<string | null>(null);

  const [showCancel, setShowCancel] = useState(false);
  const [showReprice, setShowReprice] = useState(false);
  const [repriceNote, setRepriceNote] = useState<string | null>(null);
  // What Allocate automatically did, or why it did nothing.
  const [autoNote, setAutoNote] = useState<{ ok: boolean; text: string } | null>(null);

  // Releasing an allocation: asked first, then sent.
  const [releaseAsk, setReleaseAsk] = useState<{ allocation: NurseryOrderLineAllocation; line: NurseryOrderLine } | null>(null);
  const [releasingId, setReleasingId] = useState<number | null>(null);
  const [releaseError, setReleaseError] = useState<string | null>(null);
  const [confirmError, setConfirmError] = useState<string | null>(null);

  // Undo picking: every pick on the order taken back, so it can be edited again.
  const [showUndoPicking, setShowUndoPicking] = useState(false);
  const [undoPickingBusy, setUndoPickingBusy] = useState(false);
  const [undoPickingError, setUndoPickingError] = useState<string | null>(null);
  const [undoPickingNote, setUndoPickingNote] = useState<string | null>(null);

  // Refund retry
  const [refundRetrying, setRefundRetrying] = useState(false);
  const [refundError, setRefundError] = useState<string | null>(null);
  // A refund of a cash sale, handed back from a till.
  const [showCashRefund, setShowCashRefund] = useState(false);
  // A credit note on one of the order's invoices: a nursery manager asks, an
  // org admin or an Accounts Admin approves (Rob, 2026-10-13).
  const [creditAccess, setCreditAccess] = useState<CreditRequestAccess | null>(null);
  const [creditRequests, setCreditRequests] = useState<CreditNoteRequest[]>([]);
  const [creditFor, setCreditFor] = useState<{ id: number; invoice_number: string; balance: number } | null>(null);
  const [creditNotice, setCreditNotice] = useState<string | null>(null);

  const loadOrder = async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await nurseryService.getOrder(Number(id));
      if (res.success) {
        setOrder(res.data);
        if (res.data?.invoices?.some((inv) => inv.status === 'sent' || inv.status === 'paid')) loadCreditRequests(res.data.id);
      } else {
        setError(t('notFound'));
      }
    } catch (e: any) {
      setError(e?.message || t('loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadOrder(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Never stops the order showing: without an answer there is no credit button.
  const loadCreditRequests = async (orderId: number) => {
    try {
      const a = await nurseryAccountsService.getCreditRequestAccess();
      if (!a.success || !a.data) return;
      setCreditAccess(a.data);
      if (!a.data.can_request && !a.data.can_approve) return;
      const r = await nurseryAccountsService.getCreditRequests({ order_id: orderId });
      if (r.success && r.data) setCreditRequests(r.data);
    } catch { /* no credit button */ }
  };

  const handleConfirm = async () => {
    if (!order) return;
    setActionLoading(true);
    setConfirmError(null);
    try {
      // The account rules may refuse it (ACCOUNT_REFUSED); say why, never swallow it.
      const res = await nurseryService.updateOrder(order.id, { status: 'confirmed' } as any);
      if (res && res.success === false) setConfirmError(errorMessage(res, t('confirmFailed')));
      loadOrder();
    } catch (e) {
      setConfirmError(errorMessage(e, t('confirmFailed')));
    }
    setActionLoading(false);
  };

  // Stock can still be added to an order being picked, or picked, while its
  // load has not gone: it becomes another card on the pick list. A picked
  // order goes back to picking, which withdraws the invoice picking raised.
  const isTopUp = !!order && ['picking', 'picked'].includes(order.status)
    && !!order.load_status && ['released', 'picking', 'loaded'].includes(order.load_status);
  // On a load the pickers pick without allocating, nothing is allocated ahead.
  const onFreeLoad = !!order && order.load_pick_mode === 'free'
    && !!order.load_status && !['despatched', 'cancelled'].includes(order.load_status);
  const canAllocate = !!order && !onFreeLoad && (['confirmed', 'partially_allocated'].includes(order.status)
    || (isTopUp && !!order.lines?.some(l => l.quantity_allocated < l.quantity_ordered)));

  const allocLine = order?.lines?.find(l => l.id === allocLineId) ?? null;
  const allocRemaining = allocLine ? Math.max(allocLine.quantity_ordered - allocLine.quantity_allocated, 0) : 0;
  const allocLot = availableStock?.find(s => s.id === allocLotId) ?? null;
  const allocMax = allocLot ? Math.min(allocRemaining, Number(allocLot.available_count)) : allocRemaining;

  const handleOpenAllocate = async (lineId: number) => {
    setAllocLineId(lineId);
    setAllocLotId('');
    setAllocQuantity('');
    setAllocError(null);
    setAvailableStock(null);
    setShowAllocate(true);
    try {
      // By line, so a line sold under an umbrella product is offered the lots
      // of every stock item it may be filled from.
      const res = await nurseryService.getAvailableStock({ orderLineId: lineId });
      setAvailableStock(res.success ? res.data || [] : []);
      if (!res.success) setAllocError(errorMessage(res, t('lotsFailed')));
    } catch (e) {
      setAvailableStock([]);
      setAllocError(errorMessage(e, t('lotsFailed')));
    }
  };

  const handleSelectLot = (lotId: number | '') => {
    setAllocLotId(lotId);
    const lot = availableStock?.find(s => s.id === lotId);
    // Offer as much as both the line and the lot can take.
    setAllocQuantity(lot ? Math.min(allocRemaining, Number(lot.available_count)) : '');
  };

  const handleAllocate = async () => {
    if (!allocLineId || !allocLotId || !allocQuantity) return;
    setAllocSubmitting(true);
    setAllocError(null);
    try {
      const res = await nurseryService.allocate({
        order_line_id: allocLineId,
        lot_id: Number(allocLotId),
        quantity: Number(allocQuantity),
      });
      if (res.success) {
        setShowAllocate(false);
        loadOrder();
      } else {
        setAllocError(errorMessage(res, t('allocateFailed')));
      }
    } catch (e) {
      setAllocError(errorMessage(e, t('allocateFailed')));
    }
    setAllocSubmitting(false);
  };

  // Fill the order from despatchable places, oldest ready first. A confirmed
  // order was already tried when it was confirmed; this catches stock since.
  const handleAutoAllocate = async () => {
    if (!order) return;
    setActionLoading(true);
    setAutoNote(null);
    try {
      const res = await nurseryService.autoAllocate([order.id]);
      if (!res.success) {
        setAutoNote({ ok: false, text: errorMessage(res, t('autoFailed')) });
      } else {
        const r = res.data;
        const mine = r.orders[0];
        const trees = mine?.allocated.reduce((t, a) => t + a.quantity, 0) ?? 0;
        const short = mine?.short.reduce((t, s) => t + s.quantity, 0) ?? 0;
        setAutoNote(r.no_places
          ? { ok: false, text: t('autoNoPlaces') }
          : trees
            ? { ok: true, text: `${t('autoAllocated', { count: trees })}${short ? t('autoShort', { short }) : ''}` }
            : { ok: false, text: `${t('autoNoneFree')}${short ? t('autoShort', { short }) : ''}` });
        loadOrder();
      }
    } catch (e) {
      setAutoNote({ ok: false, text: errorMessage(e, t('autoFailed')) });
    }
    setActionLoading(false);
  };

  const handleRelease = async () => {
    if (!releaseAsk) return;
    const { allocation } = releaseAsk;
    setReleasingId(allocation.id);
    setReleaseError(null);
    try {
      const res = await nurseryService.deallocate(allocation.id);
      if (res.success) {
        setReleaseAsk(null);
        await loadOrder();
      } else {
        setReleaseError(errorMessage(res, t('releaseFailed')));
      }
    } catch (e) {
      setReleaseError(errorMessage(e, t('releaseFailed')));
    }
    setReleasingId(null);
  };

  const handleUndoPicking = async () => {
    if (!order) return;
    setUndoPickingBusy(true);
    setUndoPickingError(null);
    try {
      const res = await nurseryService.undoOrderPicking(order.id);
      if (res.success) {
        setShowUndoPicking(false);
        await loadOrder();
        const { trees_returned: trees } = res.data;
        setUndoPickingNote(trees ? t('undoneTrees', { count: trees }) : t('undoneNone'));
      } else {
        setUndoPickingError(errorMessage(res, t('undoFailed')));
      }
    } catch (e) {
      setUndoPickingError(errorMessage(e, t('undoFailed')));
    }
    setUndoPickingBusy(false);
  };

  const handleOpenInvoice = () => {
    // Today in the org's timezone — a UTC date is yesterday before 10 AM in Melbourne.
    const today = getTodayInTz(timezone);
    setInvoiceDate(today);
    setInvoiceDueDate(addDays(today, INVOICE_TERMS_DAYS));
    setInvoiceError(null);
    setFreightChoice(EMPTY_FREIGHT_CHOICE);
    setShowInvoice(true);
  };

  // The due date follows the invoice date until someone sets it by hand.
  const handleInvoiceDateChange = (value: string) => {
    if (value && invoiceDate && invoiceDueDate === addDays(invoiceDate, INVOICE_TERMS_DAYS)) {
      setInvoiceDueDate(addDays(value, INVOICE_TERMS_DAYS));
    }
    setInvoiceDate(value);
  };

  const handleInvoicePdf = async (inv: NurseryInvoice) => {
    setPdfBusy(inv.id);
    setError(null);
    try {
      downloadBlob(await nurseryAccountsService.getInvoicePdf(inv.id), `${inv.invoice_number}.pdf`);
    } catch (e) {
      setError(errorMessage(e, t('downloadFailed')));
    }
    setPdfBusy(null);
  };

  // An order going by an outside carrier has no freight until it is invoiced.
  const byCarrier = !!order && order.despatch_method === 'carrier'
    && order.placed_by_org !== 'customer' && !(Number(order.delivery_fee) > 0);
  const freightTyped = invoiceFreight.trim() === '' ? NaN : Number(invoiceFreight);
  // Neither the customer nor its head office says what freight it pays: ask,
  // rather than let it fall silently to the delivery zone (Rob, 2026-10-16).
  // An older server sends no rule, and nothing is asked.
  const askFreight = !!order && !byCarrier && order.placed_by_org !== 'customer'
    && !(Number(order.delivery_fee) > 0) && order.account?.freight?.source === 'default';
  const freightChoiceValue = askFreight ? freightChoiceFrom(freightChoice) : null;
  const freightOk = byCarrier
    ? Number.isFinite(freightTyped) && freightTyped >= 0
    : !askFreight || freightChoiceValue != null;

  const handleCreateInvoice = async () => {
    if (!order || !invoiceDate || !freightOk) return;
    setInvoiceSubmitting(true);
    setInvoiceError(null);
    try {
      const res = await nurseryService.createInvoice({
        order_id: order.id,
        invoice_date: invoiceDate,
        ...(invoiceDueDate ? { due_date: invoiceDueDate } : {}),
        ...(byCarrier ? { freight_amount: freightTyped } : {}),
        ...(freightChoiceValue ? { freight_choice: freightChoiceValue } : {}),
      });
      if (res.success) {
        setShowInvoice(false);
        loadOrder();
      } else {
        setInvoiceError(errorMessage(res, t('invoiceFailed')));
      }
    } catch (e) {
      setInvoiceError(errorMessage(e, t('invoiceFailed')));
    }
    setInvoiceSubmitting(false);
  };

  const handleSavePo = async () => {
    if (!order || poDraft === null) return;
    const value = poDraft.trim();
    if (!value && order.account?.po_required) {
      setPoError(t('poRequired', { payer: order.account.payer_name }));
      return;
    }
    setPoSaving(true);
    setPoError(null);
    try {
      const res = await nurseryService.updateOrder(order.id, { customer_po: value || null });
      if (res.success) {
        setPoDraft(null);
        // The save response carries none of the joined fields.
        loadOrder();
      } else {
        setPoError(errorMessage(res, t('poFailed')));
      }
    } catch (e) {
      setPoError(errorMessage(e, t('poFailed')));
    }
    setPoSaving(false);
  };

  const handleRetryRefund = async () => {
    if (!order?.refund) return;
    setRefundRetrying(true);
    setRefundError(null);
    try {
      const res = await nurseryAccountsService.retryRefund(order.refund.id);
      if (res.success) loadOrder();
      else setRefundError(errorMessage(res, t('refundFailed')));
    } catch (e) {
      setRefundError(errorMessage(e, t('refundFailed')));
    }
    setRefundRetrying(false);
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="animate-pulse space-y-4">
          <div className="h-12 bg-neutral-100 rounded-lg" />
          <div className="h-32 bg-neutral-100 rounded-lg" />
          <div className="h-48 bg-neutral-100 rounded-lg" />
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-6 text-center py-16">
        <ShoppingCart className="h-12 w-12 mx-auto mb-3 text-neutral-300" />
        <p className="text-neutral-500">{t('notFound')}</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/nursery/orders')}>
          {t('back')}
        </Button>
      </div>
    );
  }

  const statusColor = STATUS_COLORS[order.status] || 'bg-neutral-100 text-neutral-700';
  // Orders are invoiced when picked (older website orders at checkout); never offer a second invoice.
  const hasLiveInvoice = (order.invoices ?? []).some(inv => inv.status !== 'cancelled');
  // The PO is printed on an authorised invoice, so it is fixed from then on.
  const poLocked = (order.invoices ?? []).some(inv => inv.status !== 'cancelled' && !!inv.authorised_at);
  const today = getTodayInTz(timezone);
  const account = order.account ?? null;
  const paidByOther = !!account && account.payer_id !== order.customer_id;
  const credit = order.credit ?? null;
  // A server with account rules says where the order stands (AccountHoldCard);
  // an older one only reports the credit position, shown as a warning.
  const creditWarning = !order.account_hold && !!credit && (credit.over_limit || num(credit.overdue_30) > 0);
  const refund = order.refund ?? null;
  // A line entered before its tree had a price. It invoices at nothing, so an
  // invoice carrying one is held until it is priced.
  const unpricedLines = (order.lines ?? []).filter(l => l.unit_price == null);
  const canReprice = canSellAdmin && !poLocked && (
    REPRICEABLE_STATUSES.includes(order.status)
    || (DESPATCHED_STATUSES.includes(order.status) && !hasLiveInvoice && order.payment_status !== 'paid')
  );
  // Picked or being picked, and its load has not gone: every pick can be taken
  // back, which is the way back to editing it.
  const canUndoPicking = canManage && ['picking', 'picked'].includes(order.status)
    && !!order.load_status && ['released', 'picking', 'loaded'].includes(order.load_status);

  return (
    <>
      <PageHeader
        title={t('title', { number: order.order_number })}
        subtitle={order.customer_name || t('customerNumber', { id: order.customer_org_id })}
        icon={<ShoppingCart className="h-6 w-6 text-white" />}
        actionButtons={
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold ${statusColor}`}>
              {t(`status.${order.status}`, { defaultValue: order.status.replace(/_/g, ' ').toUpperCase() })}
            </span>

            {canSell && (
              <Button variant="outline" size="sm" onClick={() => navigate('/nursery/orders/new')}>
                <Plus className="h-4 w-4" />
                {t('newOrder')}
              </Button>
            )}

            {canUndoPicking && (
              <Button variant="outline" size="sm" onClick={() => { setUndoPickingError(null); setShowUndoPicking(true); }}>
                <Undo2 className="h-4 w-4" />
                {t('undoPicking')}
              </Button>
            )}

            {canSell && EDITABLE_STATUSES.includes(order.status) && (
              <Button variant="outline" size="sm" onClick={() => navigate(`/nursery/orders/new?order=${order.id}`)}>
                <ListPlus className="h-4 w-4" />
                {t('changeQuantities')}
              </Button>
            )}

            {canSell && order.status === 'draft' && (
              <Button variant="primary" size="sm" onClick={handleConfirm} disabled={actionLoading}>
                <CheckCircle className="h-4 w-4" />
                {actionLoading ? t('confirming') : t('confirm')}
              </Button>
            )}

            {canAllocate && (
              <Button variant="outline" size="sm" onClick={() => {
                const line = order.lines?.find(l => l.quantity_allocated < l.quantity_ordered);
                if (line) handleOpenAllocate(line.id);
              }}>
                <Package className="h-4 w-4" />
                {t('allocate')}
              </Button>
            )}

            {canManage && !onFreeLoad && ['confirmed', 'partially_allocated'].includes(order.status) && (
              <Button variant="outline" size="sm" onClick={handleAutoAllocate} disabled={actionLoading}>
                <Wand2 className="h-4 w-4" />
                {t('allocateAuto')}
              </Button>
            )}

            {canReprice && (
              <Button variant="outline" size="sm" onClick={() => { setRepriceNote(null); setShowReprice(true); }}>
                <Tag className="h-4 w-4" />
                {t('updatePrices')}
              </Button>
            )}

            {canSellAdmin && CANCELLABLE_STATUSES.includes(order.status) && !hasLiveInvoice && (
              <Button variant="outline" size="sm" onClick={() => setShowCancel(true)}>
                <XCircle className="h-4 w-4" />
                {t('cancelOrder')}
              </Button>
            )}

            {canAccounts && ['allocated', 'picked', 'shipped', 'completed', 'invoiced'].includes(order.status) && !hasLiveInvoice && (
              <Button variant="outline" size="sm" onClick={handleOpenInvoice}>
                <FileText className="h-4 w-4" />
                {t('createInvoice')}
              </Button>
            )}
          </div>
        }
      />
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Header */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {order.status === 'cancelled' && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 break-words">
            {order.cancelled_at ? t('cancelledOn', { date: formatDateInTz(order.cancelled_at, timezone) }) : t('cancelled')}
            {order.cancel_reason ? ` ${order.cancel_reason}` : ''}
          </div>
        )}

        {refund?.status === 'succeeded' && (
          <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 text-sm text-green-900 break-words">
            {refund.completed_at
              ? t(refund.method === 'cash' ? 'refundDoneCashOn' : 'refundDoneCardOn', { amount: money(refund.amount), date: formatDateInTz(refund.completed_at, timezone) })
              : t(refund.method === 'cash' ? 'refundDoneCash' : 'refundDoneCard', { amount: money(refund.amount) })}
          </div>
        )}
        {refund?.status === 'cash_due' && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-900 space-y-2 break-words">
            <p className="font-semibold">{t('cashDue', { amount: money(refund.amount) })}</p>
            <p>{t('cashDueNote')}</p>
            {canManage && (
              <Button variant="outline" size="sm" onClick={() => setShowCashRefund(true)}>{t('giveBack')}</Button>
            )}
          </div>
        )}
        {refund?.status === 'pending' && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-900 break-words">
            {t('refundPending', { amount: money(refund.amount) })}
          </div>
        )}
        {refund?.status === 'failed' && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">
            <p className="font-semibold break-words">
              {t('refundFailedNote', { amount: money(refund.amount) })}
            </p>
            <p className="break-words">{refundError || refund.error || t('noReason')}</p>
            {isAccountsAdmin && (
              <div className="mt-2">
                <Button variant="outline" size="sm" onClick={handleRetryRefund} disabled={refundRetrying}>
                  {refundRetrying ? t('trying') : t('tryAgain')}
                </Button>
              </div>
            )}
          </div>
        )}
        {!refund && num(order.refund_due) > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-900 break-words">
            {t('refundDue', { amount: money(order.refund_due) })}
          </div>
        )}

        {order.customer_is_walk_in && (canManage || canSell) && order.status !== 'cancelled' && (
          <CounterPaymentCard orderId={order.id} timezone={timezone} onPaid={loadOrder} />
        )}

        {autoNote && (
          <div className={`${autoNote.ok ? 'bg-green-50 border-green-200 text-green-900' : 'bg-amber-50 border-amber-200 text-amber-900'} border rounded-lg px-4 py-3 text-sm break-words`}>
            {autoNote.text}
            {!autoNote.ok && <> <Link to="/nursery/despatchable" className="underline">{t('despatchableLink')}</Link></>}
          </div>
        )}

        {undoPickingNote && (
          <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 text-sm text-green-900 break-words" role="status">
            {undoPickingNote}
          </div>
        )}

        {repriceNote && (
          <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 text-sm text-green-900 break-words">
            {repriceNote}
          </div>
        )}

        {unpricedLines.length > 0 && order.status !== 'cancelled' && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-900 break-words">
            <p className="font-semibold">
              {t('unpriced', { count: unpricedLines.length })}
            </p>
            <p>
              {poLocked
                ? t('unpricedLocked')
                : t('unpricedOpen')}
            </p>
            {canReprice && (
              <div className="mt-2">
                <Button variant="outline" size="sm" onClick={() => { setRepriceNote(null); setShowReprice(true); }}>
                  {t('updatePrices')}
                </Button>
              </div>
            )}
          </div>
        )}

        {confirmError && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 break-words">{confirmError}</div>
        )}

        {order.account_hold && (
          <AccountHoldCard
            orderId={order.id}
            hold={order.account_hold}
            editable={!['shipped', 'invoiced', 'completed', 'cancelled'].includes(order.status)}
            onChange={(hold) => { setOrder((o) => (o ? { ...o, account_hold: hold } : o)); setConfirmError(null); }}
          />
        )}

        {/* Deposits are the Accounts module's; the card reads its own state. */}
        {canAccounts && order.account?.deposit_required && <OrderDepositCard orderId={order.id} />}

        {creditWarning && credit && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-900 break-words">
            {credit.over_limit && (
              <p>
                <Trans
                  t={t}
                  i18nKey="overLimit"
                  values={{ payer: credit.payer_name, balance: money(credit.balance), limit: money(credit.credit_limit) }}
                  components={{ b: <span className="font-semibold" /> }}
                />
              </p>
            )}
            {num(credit.overdue_30) > 0 && (
              <p>
                {credit.over_limit
                  ? t('overdueAlso', { amount: money(credit.overdue_30) })
                  : (
                    <Trans
                      t={t}
                      i18nKey="overdue"
                      values={{ payer: credit.payer_name, amount: money(credit.overdue_30) }}
                      components={{ b: <span className="font-semibold" /> }}
                    />
                  )}
              </p>
            )}
            <p className="text-xs mt-1">{t('warningOnly')}</p>
          </div>
        )}

        {/* Order details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white border border-neutral-200 rounded-xl p-5 space-y-3">
            <h3 className="text-sm font-semibold text-neutral-600 uppercase tracking-wide">{t('orderDetails')}</h3>

            <DetailRow icon={<Calendar className="h-4 w-4 text-neutral-400" />} label={t('orderDate')}>
              {formatLocalDateInTz(order.order_date, timezone)}
            </DetailRow>

            <DetailRow icon={<User className="h-4 w-4 text-neutral-400" />} label={t('customer')}>
              {order.customer_name || t('orgNumber', { id: order.customer_org_id })}
            </DetailRow>

            {paidByOther && account && (
              <DetailRow icon={<Landmark className="h-4 w-4 text-neutral-400" />} label={t('account')}>
                {t('paidBy', { payer: account.payer_name })}
              </DetailRow>
            )}

            <DetailRow icon={<Hash className="h-4 w-4 text-neutral-400" />} label={t('poNumber')}>
              {poDraft === null ? (
                <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <span className={order.customer_po ? '' : 'font-normal text-neutral-500'}>
                    {order.customer_po || t('noneGiven')}
                  </span>
                  {canSell && !poLocked && (
                    <button type="button" aria-label={t('changePoAria')}
                      className="text-xs font-normal text-primary-600 hover:underline"
                      onClick={() => { setPoError(null); setPoDraft(order.customer_po ?? ''); }}>
                      {t('change')}
                    </button>
                  )}
                </span>
              ) : (
                <span className="block space-y-2">
                  <label htmlFor="order-po" className="sr-only">{t('customerPo')}</label>
                  <input id="order-po" type="text" maxLength={PO_MAX} value={poDraft}
                    onChange={e => setPoDraft(e.target.value)}
                    className="w-full border border-neutral-300 rounded-lg px-3 py-1.5 text-sm font-normal focus:outline-none focus:ring-2 focus:ring-primary-500" />
                  <span className="flex flex-wrap gap-2">
                    <Button size="sm" onClick={handleSavePo} disabled={poSaving}>{poSaving ? t('saving') : t('save')}</Button>
                    <Button size="sm" variant="outline" onClick={() => setPoDraft(null)} disabled={poSaving}>{t('cancel')}</Button>
                  </span>
                </span>
              )}
              {poError && <span className="block text-xs font-normal text-red-600 mt-1">{poError}</span>}
              {poLocked && (
                <span className="block text-xs font-normal text-neutral-500 mt-1">
                  {t('poLocked')}
                </span>
              )}
              {!poLocked && !order.customer_po && account?.po_required && (
                <span className="block text-xs font-normal text-amber-700 mt-1">
                  {t('poRequired', { payer: account.payer_name })}
                </span>
              )}
            </DetailRow>

            {order.requested_delivery_date && (
              <DetailRow icon={<Truck className="h-4 w-4 text-neutral-400" />} label={t('requested')}>
                {formatLocalDateInTz(order.requested_delivery_date, timezone)}
              </DetailRow>
            )}

            {order.estimated_delivery_date && (
              <DetailRow icon={<Truck className="h-4 w-4 text-neutral-400" />} label={t('estDelivery')}>
                {formatLocalDateInTz(order.estimated_delivery_date, timezone)}
              </DetailRow>
            )}

            <DetailRow icon={<ShoppingCart className="h-4 w-4 text-neutral-400" />} label={t('placedBy')}>
              {order.placed_by_org === 'nursery' ? t('placedByNursery') : t('placedByCustomer')}
            </DetailRow>
          </div>

          <div className="bg-white border border-neutral-200 rounded-xl p-5 space-y-3">
            <h3 className="text-sm font-semibold text-neutral-600 uppercase tracking-wide">{t('summary')}</h3>

            <div className="grid grid-cols-2 gap-3">
              <div className="bg-primary-50 rounded-lg p-3 text-center">
                <p className="text-xl font-bold text-primary-700">{order.total_trees.toLocaleString()}</p>
                <p className="text-xs font-medium text-primary-600">{t('totalTrees')}</p>
              </div>
              <div className="bg-green-50 rounded-lg p-3 text-center">
                <p className="text-xl font-bold text-green-700">
                  ${Number(order.total_value || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </p>
                <p className="text-xs font-medium text-green-600">{t('totalValue')}</p>
              </div>
            </div>

            {order.notes && (
              <div className="pt-2 border-t border-neutral-100">
                <p className="text-xs text-neutral-500 mb-1">{t('notes')}</p>
                <p className="text-sm text-neutral-700">{order.notes}</p>
              </div>
            )}
          </div>
        </div>

        <OrderDespatchCard order={order} timezone={timezone} onSaved={loadOrder} />

        {/* Order lines */}
        <div className="bg-white border border-neutral-200 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-neutral-600 uppercase tracking-wide mb-3">{t('orderLines')}</h3>
          {order.lines && order.lines.length > 0 ? (
            <OrderLineTable
              lines={order.lines}
              onAllocate={canAllocate ? handleOpenAllocate : undefined}
              onRelease={canManage && RELEASABLE_STATUSES.includes(order.status)
                ? (allocation, line) => { setReleaseError(null); setReleaseAsk({ allocation, line }); }
                : undefined}
              releasingId={releasingId}
              inLine={order.status === 'confirmed' || order.status === 'partially_allocated'}
            />
          ) : (
            <p className="text-sm text-neutral-400 text-center py-6">{t('noLines')}</p>
          )}
        </div>

        {/* A bulk order drawn down a bit at a time, or a draw-down from one */}
        <OrderDrawdownCard order={order} canManage={canSell} onChanged={loadOrder} />

        {/* Backorders: where a short despatch carried the rest of this order */}
        {order.backorders && order.backorders.length > 0 && (
          <div className="bg-white border border-neutral-200 rounded-xl p-5">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <h3 className="text-sm font-semibold text-neutral-600 uppercase tracking-wide">{t('backorders')}</h3>
              <Link to="/nursery/orders?backorders=true" className="text-sm text-primary-700 hover:underline">{t('allBackorders')}</Link>
            </div>
            <div className="space-y-2">
              {order.backorders.map(b => (
                <Link key={b.id} to={`/nursery/orders/${b.id}`}
                  className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 border border-neutral-100 rounded-lg px-4 py-3 hover:border-primary-300">
                  <span className="text-sm font-medium text-primary-700 break-words">{b.order_number}</span>
                  <span className="text-sm text-neutral-600">
                    {t('backorderTrees', { count: b.trees, trees: b.trees.toLocaleString() })} · {t(`statusLower.${b.status}`, { defaultValue: b.status.replace(/_/g, ' ') })}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* What went when, across the whole backorder chain */}
        <OrderShipmentsCard order={order} timezone={timezone} />

        {/* Notes, follow-ups and issues: internal, never shown to the customer */}
        {hasCrm && (
          <CrmPanel key={order.id} subject={{ orderId: order.id }} heading="Notes and issues"
            lineChoices={(order.lines ?? []).map((l) => ({
              value: l.id,
              label: [l.product_name || l.cultivar_name, l.rootstock_name, l.tree_type_name].filter(Boolean).join(' · ') || `Line ${l.id}`,
            }))} />
        )}

        {/* Invoices */}
        {order.invoices && order.invoices.length > 0 && (
          <div className="bg-white border border-neutral-200 rounded-xl p-5">
            <h3 className="text-sm font-semibold text-neutral-600 uppercase tracking-wide mb-3">{t('invoices')}</h3>
            {creditNotice && (
              <div className="bg-green-50 border border-green-200 rounded-lg px-3 py-2 text-sm text-green-800 break-words mb-3">{creditNotice}</div>
            )}
            <div className="space-y-2">
              {order.invoices.map(inv => {
                const balance = num(inv.total_amount) - num(inv.amount_paid) - num(inv.amount_credited);
                const isVoid = inv.status === 'cancelled';
                const body = (
                  <>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-primary-700 break-words">{inv.invoice_number}</p>
                      <p className="text-xs text-neutral-500">
                        {formatLocalDateInTz(inv.invoice_date.slice(0, 10), timezone)}
                        {inv.due_date ? t('due', { date: formatLocalDateInTz(inv.due_date.slice(0, 10), timezone) }) : ''}
                      </p>
                    </div>
                    <div className="text-right ml-auto">
                      <p className="text-sm text-neutral-600">
                        <Trans t={t} i18nKey="invoiceTotal" values={{ amount: money(inv.total_amount) }} components={{ amount: <span className="font-medium text-neutral-800" /> }} />
                      </p>
                      {!isVoid && (
                        <p className="text-sm text-neutral-600">
                          <Trans t={t} i18nKey="stillOwing" values={{ amount: money(balance) }} components={{ amount: <span className="font-semibold text-neutral-900" /> }} />
                        </p>
                      )}
                      <PaymentStateBadge state={invoicePaymentState(inv, today)} className="mt-1" />
                    </div>
                  </>
                );
                const rowCls = 'flex flex-wrap items-center justify-between gap-x-4 gap-y-1 border border-neutral-100 rounded-lg px-4 py-3';
                return canAccounts ? (
                  <Link key={inv.id} to={`/accounts/invoices/${inv.id}`} className={`${rowCls} hover:border-primary-300`}>
                    {body}
                  </Link>
                ) : (
                  <div key={inv.id} className={rowCls}>
                    {body}
                    <div className="basis-full flex flex-wrap items-center gap-2">
                      <Button variant="outline" size="sm" disabled={pdfBusy === inv.id} onClick={() => handleInvoicePdf(inv)}>
                        <Download className="h-4 w-4" />
                        {pdfBusy === inv.id ? t('downloading') : t('downloadPdf')}
                      </Button>
                      {(() => {
                        const waiting = creditRequests.find((r) => r.invoice_id === inv.id && r.status === 'pending');
                        if (waiting) {
                          return (
                            <Link to={`/accounts/credit-note-requests?request=${waiting.id}`} className="text-sm text-amber-800 underline break-words">
                              {t('creditWaiting', { amount: money(waiting.total_amount) })}
                            </Link>
                          );
                        }
                        const creditable = (inv.status === 'sent' || inv.status === 'paid') && balance > 0.004;
                        if (!creditable || !(creditAccess?.can_request || creditAccess?.can_approve)) return null;
                        return (
                          <Button variant="outline" size="sm"
                            onClick={() => setCreditFor({ id: inv.id, invoice_number: inv.invoice_number, balance })}>
                            {creditAccess.can_approve ? t('raiseCredit') : t('askCredit')}
                          </Button>
                        );
                      })()}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {creditFor && (
          <CreditNoteDialog
            mode={creditAccess?.can_approve ? 'raise' : 'request'}
            invoice={creditFor}
            onClose={() => setCreditFor(null)}
            onDone={() => {
              setCreditNotice(creditAccess?.can_approve ? t('creditRaised') : t('creditRequested'));
              setCreditFor(null);
              loadOrder();
            }}
          />
        )}

        {showReprice && (
          <RepriceOrderDialog
            orderId={order.id}
            orderNumber={order.order_number}
            onClose={() => setShowReprice(false)}
            onDone={async (result) => {
              setShowReprice(false);
              await loadOrder();
              const parts = [t('repricedLines', { count: result.updated })];
              if (result.invoice_rebuilt) parts.push(t('repricedInvoice', { number: result.invoice_rebuilt }));
              if (result.still_unpriced) parts.push(t('repricedStillUnpriced', { count: result.still_unpriced }));
              if (result.updated && DESPATCHED_STATUSES.includes(order.status) && !hasLiveInvoice) {
                parts.push(t('repricedCreateInvoice'));
              }
              setRepriceNote(parts.join(' '));
            }}
          />
        )}

        {showCancel && (
          <CancelOrderDialog
            orderId={order.id}
            orderNumber={order.order_number}
            onClose={() => setShowCancel(false)}
            onCancelled={async (result) => {
              setShowCancel(false);
              // Reloading clears the page's error, so a failed refund is said after it.
              await loadOrder();
              if (result.refund?.status === 'failed') {
                setError(t('cancelledRefundFailed', { reason: result.refund.error || t('noReasonGiven') }));
              }
            }}
          />
        )}

        {showCashRefund && refund && (
          <CashRefundDialog
            refund={refund}
            onClose={() => setShowCashRefund(false)}
            onDone={async () => { setShowCashRefund(false); await loadOrder(); }}
          />
        )}

        {showUndoPicking && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6" role="dialog" aria-labelledby="undo-picking-title">
              <h3 id="undo-picking-title" className="text-base font-semibold text-neutral-800 mb-2">{t('undoTitle')}</h3>
              <div className="text-sm text-neutral-600 space-y-2 break-words">
                <p>
                  {t('undoBody1', { number: order.order_number })}
                </p>
                <p>
                  {t('undoBody2', { load: order.load_number ?? t('itsLoad') })}
                </p>
              </div>
              {undoPickingError && (
                <div className="mt-3 bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700 break-words">
                  {undoPickingError}
                </div>
              )}
              <div className="flex flex-wrap justify-end gap-2 mt-4">
                <Button variant="outline" size="sm" onClick={() => setShowUndoPicking(false)} disabled={undoPickingBusy}>{t('keepPicks')}</Button>
                <Button variant="danger" size="sm" onClick={handleUndoPicking} disabled={undoPickingBusy}>
                  {undoPickingBusy ? t('undoing') : t('undoPicking')}
                </Button>
              </div>
            </div>
          </div>
        )}

        {releaseAsk && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6" role="dialog" aria-labelledby="release-title">
              <h3 id="release-title" className="text-base font-semibold text-neutral-800 mb-2">{t('releaseTitle')}</h3>
              <p className="text-sm text-neutral-600 break-words">
                {t('releaseBody', {
                  count: releaseAsk.allocation.quantity,
                  lot: releaseAsk.allocation.lot_code || `#${releaseAsk.allocation.lot_id}`,
                  ordered: releaseAsk.line.quantity_ordered,
                })}
              </p>
              {releaseError && (
                <div className="mt-3 bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700 break-words">
                  {releaseError}
                </div>
              )}
              <div className="flex justify-end gap-2 mt-4">
                <Button variant="outline" size="sm" onClick={() => setReleaseAsk(null)} disabled={releasingId != null}>{t('keepIt')}</Button>
                <Button variant="danger" size="sm" onClick={handleRelease} disabled={releasingId != null}>
                  {releasingId != null ? t('releasing') : t('release')}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Allocate modal */}
        {showAllocate && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
              <h3 className="text-base font-semibold text-neutral-800 mb-1">{t('allocateStock')}</h3>
              {allocLine && (
                <p className="text-sm text-neutral-500 mb-4 break-words">
                  {allocLine.product_name
                    ? t('anyStockItem', { name: allocLine.product_name })
                    : [allocLine.cultivar_name, allocLine.rootstock_name, allocLine.tree_type_name || allocLine.tree_type_code].filter(Boolean).join(' / ')}
                  {' · '}{t('stillToAllocate', { remaining: allocRemaining, ordered: allocLine.quantity_ordered })}
                </p>
              )}
              {isTopUp && (
                <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4 break-words">
                  {order.status === 'picked'
                    ? t('topUpPicked', { load: order.load_number ?? t('itsLoad') })
                    : t('topUpPicking', { load: order.load_number ?? t('itsLoad') })}
                </p>
              )}
              <div className="space-y-3">
                <div>
                  <label htmlFor="alloc-lot" className="block text-sm font-medium text-neutral-700 mb-1">{t('readyLot')}</label>
                  {availableStock === null ? (
                    <p className="text-sm text-neutral-400">{t('loadingLots')}</p>
                  ) : availableStock.length === 0 ? (
                    <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                      {allocLine?.product_name
                        ? t('noLotsProduct', { name: allocLine.product_name })
                        : t('noLots')}
                      {t('readyStage')}
                    </p>
                  ) : (
                    <select
                      id="alloc-lot"
                      value={allocLotId}
                      onChange={e => handleSelectLot(e.target.value ? Number(e.target.value) : '')}
                      className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="">{t('selectLot')}</option>
                      {availableStock.map(s => (
                        <option key={s.id} value={s.id}>
                          {s.lot_code}
                          {allocLine?.product_name && s.cultivar_name
                            ? ` · ${[s.cultivar_name, s.rootstock_name].filter(Boolean).join(' / ')}`
                            : ''}
                          {s.location_name ? ` · ${s.location_name}` : ''} · {t('available', { count: s.available_count })}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
                <div>
                  <label htmlFor="alloc-qty" className="block text-sm font-medium text-neutral-700 mb-1">{t('quantity')}</label>
                  <input
                    id="alloc-qty"
                    type="number"
                    min="1"
                    max={allocMax || undefined}
                    value={allocQuantity}
                    onChange={e => setAllocQuantity(e.target.value === '' ? '' : parseInt(e.target.value))}
                    className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>
              {allocError && (
                <div className="mt-3 bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700">
                  {allocError}
                </div>
              )}
              <div className="flex justify-end gap-2 mt-4">
                <Button variant="outline" size="sm" onClick={() => setShowAllocate(false)}>{t('cancel')}</Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleAllocate}
                  disabled={!allocLotId || !allocQuantity || allocSubmitting}
                >
                  {allocSubmitting ? t('allocating') : t('allocate')}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Invoice modal */}
        {showInvoice && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className={`bg-white rounded-xl shadow-xl w-full p-6 max-h-[92vh] overflow-y-auto ${askFreight ? 'max-w-lg' : 'max-w-sm'}`}>
              <h3 className="text-base font-semibold text-neutral-800 mb-4">{t('createInvoice')}</h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">{t('invoiceDate')}</label>
                  <input
                    type="date"
                    value={invoiceDate}
                    onChange={e => handleInvoiceDateChange(e.target.value)}
                    className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">{t('dueDate')}</label>
                  <input
                    type="date"
                    value={invoiceDueDate}
                    onChange={e => setInvoiceDueDate(e.target.value)}
                    className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                {byCarrier && (
                  <div>
                    <label htmlFor="invoice-freight" className="block text-sm font-medium text-neutral-700 mb-1">{t('carrierFreight')}</label>
                    <input
                      id="invoice-freight"
                      type="number"
                      min="0"
                      step="0.01"
                      inputMode="decimal"
                      value={invoiceFreight}
                      onChange={e => setInvoiceFreight(e.target.value)}
                      className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                    <p className="text-xs text-neutral-500 mt-1">{t('carrierFreightHint')}</p>
                  </div>
                )}
                {askFreight && (
                  <FreightChoiceFields
                    value={freightChoice}
                    onChange={setFreightChoice}
                    customerName={order?.customer_name ?? order?.account?.payer_name ?? ''}
                    idPrefix="order-invoice-freight"
                  />
                )}
              </div>
              {invoiceError && (
                <div className="mt-3 bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700">
                  {invoiceError}
                </div>
              )}
              <div className="flex justify-end gap-2 mt-4">
                <Button variant="outline" size="sm" onClick={() => setShowInvoice(false)}>{t('cancel')}</Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleCreateInvoice}
                  disabled={!invoiceDate || invoiceSubmitting || !freightOk}
                >
                  {invoiceSubmitting ? t('creating') : t('createInvoice')}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

function DetailRow({
  icon,
  label,
  children,
}: {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-2 text-sm">
      <span className="mt-0.5 flex-shrink-0">{icon}</span>
      <span className="text-neutral-500 min-w-[80px]">{label}</span>
      <span className="font-medium text-neutral-800 break-words min-w-0 flex-1">{children}</span>
    </div>
  );
}

export default OrderDetail;
